import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnProps } from 'antd/lib/table';
import { Button, message, Table } from 'antd';
import { EllipsisOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { useUIDispatchContext } from 'providers/UI';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { useNavigate } from 'react-router-dom';
import {
  deleteAppointment,
  fetchAdminCreator,
  openAppointmentRoom
} from 'services/api/appointments';
import { createMeetingRoom } from 'services/api/meetings';
import { AppointmentListRow } from 'types/api/appointments';

import useOrgEffect from 'hooks/useOrgEffect';
import SessionsPay from './Modals';
import { ActionMenu, PageWrapper } from 'components/ui';
import useCopyText from 'hooks/useCopyText';
import { showErrorMessage } from 'utils/showErrorMessage';
import { money } from 'utils/price';
import { Private, OrganizationAlert } from 'components/shared';
import PaymentInfo from './PaymentInfo';

const PAGE_LIMIT = 10;

const Sessions: FC = () => {
  const { t } = useTranslation();
  const { toggleModal } = useUIDispatchContext();
  const { copyText } = useCopyText();
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const [results, setResults] = useState<AppointmentListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchAdminCreator({
          page,
          limit: PAGE_LIMIT
        });

        if (cancelled) return;
        setResults(res.results);
        setTotal(res.total);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [page, refreshKey]);

  const onDeleteAppointment = useCallback(
    async (id: string) => {
      try {
        await deleteAppointment(id);
        message.success(t('messages.successfully_deleted'));
        setPage(0);
        refresh();
      } catch (err) {
        showErrorMessage(err);
      }
    },
    [refresh, t]
  );

  const onCopyRoom = useCallback(
    async (id: string) => {
      try {
        const room = await openAppointmentRoom(id);
        if (!room?.redirect_url) return;
        copyText(`${room.redirect_url}/home/${room.meetUrl}`);
      } catch (err) {
        showErrorMessage(err);
      }
    },
    [copyText]
  );

  const onCopyPassword = useCallback(
    async (id: string) => {
      const room = await createMeetingRoom(id);
      if (room?.password) copyText(room.password);
    },
    [copyText]
  );

  const columns: ColumnProps<AppointmentListRow>[] = useMemo(
    () => [
      {
        title: t('sessions.id'),
        dataIndex: 'id',
        key: 'id',
        render: id => id
      },
      {
        title: 'Hash',
        dataIndex: 'hashId',
        key: 'hashId',
        render: hashId => hashId
      },
      {
        title: 'Payment Info',
        dataIndex: 'patient_payment_info',
        key: 'patient_payment_info',
        render: (_, record) => <PaymentInfo appointmentId={`${record.id}`} />
      },
      {
        title: 'Type',
        dataIndex: 'patientPaymentType',
        key: 'patientPaymentType',
        render: patientPaymentType => patientPaymentType
      },
      {
        title: t('sessions.price'),
        dataIndex: 'price',
        key: 'price',
        render: price => money(price)
      },
      {
        title: 'Paid',
        dataIndex: 'paid',
        key: 'paid',
        render(value) {
          return value ? 'Yes' : 'No';
        }
      },
      {
        title: t('sessions.notes'),
        dataIndex: 'reason',
        key: 'notes'
      },
      {
        title: t('sessions.created'),
        dataIndex: 'created_at',
        key: 'created_at',
        width: '15%',
        render: createdAt => dayjs(createdAt).format(FULL_DATE_FORMAT)
      },
      {
        title: t('sessions.actions'),
        dataIndex: 'id',
        key: 'actions',
        width: '5%',
        align: 'right',
        render: (id, record) => (
          <ActionMenu
            data={[
              {
                title: t('sessions.copy_payment'),
                permission: {
                  route: PERMISSION_ROUTES.sessionsPaid,
                  action: PERMISSION_ACTIONS.generateUrl
                },
                action() {
                  copyText(record.url || '');
                }
              },
              {
                title: t('sessions.copy'),
                permission: {
                  route: PERMISSION_ROUTES.sessionsPaid,
                  action: PERMISSION_ACTIONS.generateUrl
                },
                action() {
                  onCopyRoom(id);
                }
              },
              {
                title: 'Copy password',
                permission: {
                  route: PERMISSION_ROUTES.sessionsPaid,
                  action: PERMISSION_ACTIONS.generateUrl
                },
                action() {
                  onCopyPassword(id);
                }
              },
              {
                title: 'Logs',
                permission: {
                  route: PERMISSION_ROUTES.super,
                  action: PERMISSION_ACTIONS.read
                },
                action() {
                  navigate(`/logs/meetings?search=${id}`);
                }
              },
              {
                title: t('table.delete'),
                permission: {
                  route: PERMISSION_ROUTES.sessionsPaid,
                  action: PERMISSION_ACTIONS.delete
                },
                action() {
                  onDeleteAppointment(id);
                },
                menuProps: {
                  style: {
                    color: 'var(--danger-color)'
                  }
                }
              }
            ]}
            placement="bottomRight"
            icon={<EllipsisOutlined />}
          />
        )
      }
    ],
    [copyText, navigate, onCopyPassword, onCopyRoom, onDeleteAppointment, t]
  );

  return (
    <PageWrapper
      title="Paid Calls"
      color="white"
      extra={[
        <Private
          key="add-sessions-button"
          route={PERMISSION_ROUTES.sessionsPaid}
          action={PERMISSION_ACTIONS.create}
        >
          <OrganizationAlert>
            <Button
              type="primary"
              onClick={() => toggleModal('sessionPay', true)}
              loading={false}
            >
              <PlusOutlined />
              {t('sessions.create')}
            </Button>
          </OrganizationAlert>
        </Private>
      ]}
    >
      <Table
        dataSource={results}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination={{
          total,
          pageSize: PAGE_LIMIT,
          onChange: p => setPage(p),
          showSizeChanger: false
        }}
      />
      <SessionsPay onSaved={refresh} />
    </PageWrapper>
  );
};

export default Sessions;
